import { Button } from '@/components/ui';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/Card';

interface SlackTestMessageCardProps {
  isConnected: boolean;
  sendingTest: boolean;
  testEmail: string;
  testResult: { success: boolean; error?: string } | null;
  onTestEmailChange: (value: string) => void;
  onSendTest: () => void;
}

export function SlackTestMessageCard({
  isConnected,
  sendingTest,
  testEmail,
  testResult,
  onTestEmailChange,
  onSendTest,
}: Readonly<SlackTestMessageCardProps>) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Test Message</CardTitle>
        <CardDescription>
          Send a test DM to verify the bot can reach a Slack account. Leave email empty to message yourself.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="flex-1">
            <label htmlFor="test-email" className="mb-1 block text-sm font-medium text-foreground">
              Recipient Email
            </label>
            <input
              id="test-email"
              type="email"
              placeholder="user@example.com (optional)"
              value={testEmail}
              onChange={(e) => onTestEmailChange(e.target.value)}
              disabled={!isConnected || sendingTest}
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50"
            />
          </div>
          <Button
            variant="secondary"
            onClick={onSendTest}
            isLoading={sendingTest}
            disabled={!isConnected || sendingTest}
          >
            Send Test Message
          </Button>
        </div>

        {testResult && (
          <div className={`mt-3 rounded-md p-3 text-sm ${
            testResult.success
              ? 'bg-success-50 text-success-700'
              : 'bg-error/10 text-error'
          }`}>
            {testResult.success
              ? 'Test message sent successfully! Check your Slack DMs.'
              : testResult.error ?? 'Failed to send test message.'}
          </div>
        )}

        {!isConnected && (
          <p className="mt-2 text-xs text-muted-foreground">
            Connect to a Slack workspace first to send test messages.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
